import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Lock, Mail, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import type { LoginCredentials } from '../shared/types';
import { ForgotPasswordModal } from '../components/ForgotPasswordModal';

export function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgot, setShowForgot] = useState(false);
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: { pathname?: string } } };

  if (isAuthenticated) {
    const to = location.state?.from?.pathname || '/';
    return <Navigate to={to} replace />;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const creds: LoginCredentials = { email, password, rememberMe };
      await login(creds);
      navigate(location.state?.from?.pathname || '/');
    } catch (err: any) {
      setError(err?.response?.data?.errore || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-b from-emerald-50/80 via-white to-slate-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md space-y-4 animate-fade-in">
        {/* Header box */}
        <div className="card-section text-center p-6 space-y-2">
          <div className="mx-auto w-16 h-16 rounded-3xl bg-linear-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-lg shadow-emerald-500/25">
            <Users className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Road Hub · Squadra</h1>
          <p className="text-xs sm:text-sm text-slate-500 max-w-xs mx-auto">
            Portale operativo per squadre tecniche e interventi su strada
          </p>
        </div>

        {/* Login form */}
        <form onSubmit={submit} className="card-section space-y-4 p-6 shadow-md">
          <div>
            <label className="label text-xs uppercase tracking-wider text-slate-500 font-bold" htmlFor="email">
              Email squadra
            </label>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <Mail className="w-5 h-5" />
              </div>
              <input
                id="email"
                className="input !pl-11 text-sm"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="squadra@azienda.it"
                autoComplete="username"
                required
              />
            </div>
          </div>

          <div>
            <label className="label text-xs uppercase tracking-wider text-slate-500 font-bold" htmlFor="password">
              Password
            </label>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <Lock className="w-5 h-5" />
              </div>
              <input
                id="password"
                className="input !pl-11 text-sm"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </div>
          </div>

          <div className="flex items-center justify-between text-xs sm:text-sm">
            <label className="flex items-center gap-2.5 text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded-md accent-emerald-600"
              />
              <span>Ricordami</span>
            </label>

            <button
              type="button"
              className="text-emerald-700 font-bold hover:underline"
              onClick={() => setShowForgot(true)}
            >
              Password dimenticata?
            </button>
          </div>

          {error && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-xs font-bold animate-slide-up">
              {error}
            </div>
          )}

          <button className="btn-primary w-full justify-center py-3.5 text-base font-bold shadow-lg hover:shadow-xl transition" type="submit" disabled={loading}>
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            <span>Accedi all'App</span>
          </button>
        </form>

        <div className="text-center text-[11px] text-slate-400 px-4">
          Accessibile da smartphone e tablet.
        </div>

        <ForgotPasswordModal
          isOpen={showForgot}
          onClose={() => setShowForgot(false)}
        />
      </div>
    </div>
  );
}